import {
  GOAL_TYPE_DEFINITIONS,
  type CompetencyGoalType,
  type GoalType,
  type GoalsCompetencyConfiguration,
  type TeamGoal,
  type UserGoal,
} from '../../shared/models';
import {
  getHabitaCompetencyTeamGoalsUrl,
  getHabitaCompetencyUrl,
  getHabitaUserGoalsUpdateUrl,
  getHabitaUsersGoalsUrl,
  getHabitaUsersUrl,
} from './habitaApiUrls';
import { HttpError, HttpRepository } from './HttpRepository';

export interface SdrGoalUser {
  created_at: string;
  email: string;
  goals: UserGoal[];
  id: string;
  name: string;
  profile: string;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface ConfigureCompetencyPayload {
  enabled_types: GoalType[];
  working_days: number;
}

export interface UpdateTeamGoalPayload {
  goal: number;
  type: GoalType;
}

export interface UpdateUserGoalPayload extends UpdateTeamGoalPayload {
  period: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function firstString(...values: unknown[]): string {
  const value = values.find((item) => typeof item === 'string' && item.trim());
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeMonth(value: unknown): string {
  const month = typeof value === 'string' ? value.trim() : '';
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? month : '';
}

function normalizeGoalType(value: unknown): GoalType | null {
  const type = firstString(value).toUpperCase();
  return GOAL_TYPE_DEFINITIONS.some((definition) => definition.type === type)
    ? type as GoalType
    : null;
}

function normalizeGoalValue(value: unknown): number | null {
  const goal = Number(value);
  return Number.isInteger(goal) && goal >= 0 ? goal : null;
}

function normalizeGoals(value: unknown): UserGoal[] {
  if (!Array.isArray(value)) return [];

  const goalsByKey = new Map<string, UserGoal>();
  value.forEach((item) => {
    if (!isRecord(item)) return;
    const competency = normalizeMonth(item.competency);
    const type = normalizeGoalType(item.type);
    const goal = normalizeGoalValue(item.goal);
    if (!competency || !type || goal === null) return;

    goalsByKey.set(`${competency}:${type}`, { competency, type, goal });
  });

  return Array.from(goalsByKey.values()).sort((left, right) => (
    left.competency.localeCompare(right.competency) ||
    left.type.localeCompare(right.type)
  ));
}

function extractRows(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (!isRecord(value)) return [];

  const data = value.data;
  const candidates = [
    value.users,
    value.items,
    value.results,
    data,
    isRecord(data) ? data.users : undefined,
    isRecord(data) ? data.items : undefined,
    isRecord(data) ? data.results : undefined,
  ];

  return candidates.find(Array.isArray) ?? [];
}

function normalizeStatus(record: Record<string, unknown>): 'ACTIVE' | 'INACTIVE' {
  if (typeof record.is_active === 'boolean') return record.is_active ? 'ACTIVE' : 'INACTIVE';
  const status = firstString(record.status).toUpperCase();
  return ['INACTIVE', 'INATIVO', 'DISABLED', 'BLOCKED'].includes(status) ? 'INACTIVE' : 'ACTIVE';
}

function normalizeUser(value: unknown): SdrGoalUser | null {
  if (!isRecord(value)) return null;
  const appMetadata = isRecord(value.app_metadata) ? value.app_metadata : {};
  const userMetadata = isRecord(value.user_metadata) ? value.user_metadata : {};
  const id = firstString(value.id, value.user_id, value.uuid);
  const email = firstString(value.email, appMetadata.email, userMetadata.email).toLowerCase();
  const name = firstString(
    value.name,
    value.display_name,
    appMetadata.display_name,
    appMetadata.full_name,
    appMetadata.name,
    userMetadata.display_name,
    userMetadata.full_name,
    userMetadata.name
  );
  const rawProfile = Array.isArray(value.profile)
    ? value.profile[0]
    : Array.isArray(appMetadata.profile)
      ? appMetadata.profile[0]
      : value.profile ?? appMetadata.profile ?? appMetadata.role ?? userMetadata.profile ?? userMetadata.role;
  const profile = firstString(rawProfile).toUpperCase();
  const goals = normalizeGoals(Array.isArray(value.goals) ? value.goals : appMetadata.goals);

  if (!id || !email || !name) return null;

  return {
    created_at: firstString(value.created_at),
    email,
    goals,
    id,
    name,
    profile,
    status: normalizeStatus(value),
  };
}

function sortUsers(users: SdrGoalUser[]): SdrGoalUser[] {
  return [...users].sort((left, right) => (
    left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' }) ||
    left.email.localeCompare(right.email, 'pt-BR', { sensitivity: 'base' })
  ));
}

function unwrapRecord(value: unknown): Record<string, unknown> {
  if (!isRecord(value)) return {};
  return isRecord(value.data) ? value.data : value;
}

function normalizeCompetencyConfiguration(
  value: unknown,
  requestedPeriod: string
): GoalsCompetencyConfiguration {
  const record = unwrapRecord(value);
  const competencyRecord = isRecord(record.competency) ? record.competency : {};
  const period = normalizeMonth(competencyRecord.period);
  const workingDays = Number(competencyRecord.working_days);

  if (!period || period !== requestedPeriod || !Number.isInteger(workingDays) || workingDays < 1 || workingDays > 31) {
    throw new Error('A API retornou uma configuração de competência inválida.');
  }

  const responseGoalTypes = Array.isArray(record.goal_types)
    ? record.goal_types.reduce<CompetencyGoalType[]>((items, item) => {
      if (!isRecord(item)) return items;
      const type = normalizeGoalType(item.type);
      if (!type) return items;
      const defaultLabel = GOAL_TYPE_DEFINITIONS.find((definition) => definition.type === type)?.label ?? type;
      items.push({
        enabled: item.enabled === true,
        label: firstString(item.label) || defaultLabel,
        type,
      });
      return items;
    }, [])
    : [];

  const goalTypes = GOAL_TYPE_DEFINITIONS.map((definition) => (
    responseGoalTypes.find((item) => item.type === definition.type) ?? {
      enabled: false,
      label: definition.label,
      type: definition.type,
    }
  ));

  const teamGoals = Array.isArray(record.team_goals)
    ? record.team_goals.reduce<TeamGoal[]>((items, item) => {
      if (!isRecord(item)) return items;
      const type = normalizeGoalType(item.type);
      const goal = normalizeGoalValue(item.goal);
      if (type && goal !== null) items.push({ type, goal });
      return items;
    }, [])
    : [];

  return {
    competency: { period, working_days: workingDays },
    goal_types: goalTypes,
    team_goals: teamGoals,
  };
}

function normalizeUpdatedUserGoal(
  value: unknown,
  requestedUserId: string,
  requestedPayload: UpdateUserGoalPayload
): UserGoal {
  const responseRecord = unwrapRecord(value);
  const goalRecord = isRecord(responseRecord.goal) ? responseRecord.goal : {};
  const userId = firstString(goalRecord.user_id);
  const competency = normalizeMonth(goalRecord.period ?? goalRecord.competency);
  const type = normalizeGoalType(goalRecord.goal_type ?? goalRecord.type);
  const goal = normalizeGoalValue(goalRecord.goal);

  if (
    userId !== requestedUserId
    || competency !== requestedPayload.period
    || type !== requestedPayload.type
    || goal !== requestedPayload.goal
  ) {
    throw new Error(
      'A API respondeu ao salvamento, mas não confirmou a meta enviada. Nenhuma alteração foi considerada persistida.'
    );
  }

  return { competency, goal, type };
}

function errorCodeFromData(value: unknown): string {
  return goalsApiErrorRecords(value)
    .map((record) => firstString(record.error_code, record.code, record.error))
    .find(Boolean) ?? '';
}

function errorMessageFromData(value: unknown): string {
  return goalsApiErrorRecords(value)
    .map((record) => firstString(record.message, record.msg, record.detail))
    .find(Boolean) ?? '';
}

function goalsApiErrorRecords(value: unknown): Record<string, unknown>[] {
  if (!isRecord(value)) return [];
  const data = isRecord(value.data) ? value.data : {};

  return [
    value,
    data,
    isRecord(value.team_goal) ? value.team_goal : {},
    isRecord(data.team_goal) ? data.team_goal : {},
    isRecord(value.goal) ? value.goal : {},
    isRecord(data.goal) ? data.goal : {},
    isRecord(value.config) ? value.config : {},
    isRecord(data.config) ? data.config : {},
  ];
}

function validateUpdatedTeamGoal(
  value: unknown,
  requestedPeriod: string,
  requestedPayload: UpdateTeamGoalPayload
): void {
  const code = errorCodeFromData(value);
  const message = errorMessageFromData(value);
  if (code) {
    if (code === 'team_goal_failed') {
      throw new Error(
        'A API recebeu a Meta do Time, mas não conseguiu persistir a alteração (team_goal_failed).'
      );
    }
    throw new Error(
      `A API de metas retornou ${code}${message ? `: ${message}` : '.'}`
    );
  }

  const responseRecord = unwrapRecord(value);
  const teamGoalRecord = isRecord(responseRecord.team_goal) ? responseRecord.team_goal : {};
  const period = normalizeMonth(teamGoalRecord.period);
  const type = normalizeGoalType(teamGoalRecord.goal_type ?? teamGoalRecord.type);
  const goal = normalizeGoalValue(teamGoalRecord.goal);

  if (
    period !== requestedPeriod
    || type !== requestedPayload.type
    || goal !== requestedPayload.goal
  ) {
    throw new Error(
      'A API respondeu ao salvamento, mas não confirmou a Meta do Time enviada. Nenhuma alteração foi considerada persistida.'
    );
  }
}

export function describeGoalsApiError(
  error: unknown,
  fallback = 'Não foi possível concluir a operação.'
): string {
  if (error instanceof HttpError) {
    const code = errorCodeFromData(error.data);
    if (code === 'team_goal_failed') {
      return 'A API recebeu a Meta do Time, mas não conseguiu persistir a alteração (team_goal_failed).';
    }
    if (error.status === 403) {
      return 'Seu perfil não possui permissão para alterar esta configuração.';
    }
    if (error.status === 422 && code === 'type_not_enabled') {
      return 'Uma das metas individuais usa um tipo que ainda não foi habilitado nesta competência.';
    }
    if (error.status === 422 && code === 'type_disabled') {
      return 'Uma das metas usa um tipo desabilitado nesta competência.';
    }
    if (error.status === 400) {
      return errorMessageFromData(error.data) || 'A API recusou os dados enviados. Revise a competência e os valores.';
    }

    return errorMessageFromData(error.data) || `${fallback} (HTTP ${error.status})`;
  }

  if (
    error instanceof TypeError &&
    error.message.toLowerCase().includes('failed to fetch')
  ) {
    return 'A API de metas não respondeu à requisição. Verifique se o endpoint está publicado e com CORS habilitado.';
  }

  return error instanceof Error && error.message.trim() ? error.message : fallback;
}

export function getSdrGoal(
  goals: UserGoal[],
  competency: string,
  type: GoalType
): number | null {
  return goals.find((goal) => goal.competency === competency && goal.type === type)?.goal ?? null;
}

export class SdrGoalsRepository {
  private httpRepository = new HttpRepository();

  async fetchUsers(params: { profile?: string; searchText?: string } = {}): Promise<SdrGoalUser[]> {
    const response = await this.httpRepository.get<unknown>(getHabitaUsersUrl(params));
    return sortUsers(
      extractRows(response.data)
        .map(normalizeUser)
        .filter((user): user is SdrGoalUser => Boolean(user))
    );
  }

  async fetchUsersWithGoals(params: { profile?: string; searchText?: string } = {}): Promise<SdrGoalUser[]> {
    const response = await this.httpRepository.get<unknown>(
      getHabitaUsersGoalsUrl(params),
      { cache: 'no-store' }
    );
    return sortUsers(
      extractRows(response.data)
        .map(normalizeUser)
        .filter((user): user is SdrGoalUser => Boolean(user))
    );
  }

  async fetchSdrGoalUsers(searchText = ''): Promise<SdrGoalUser[]> {
    const params = { profile: 'SDR', searchText: searchText || undefined };
    const [users, usersWithGoals] = await Promise.all([
      this.fetchUsers(params),
      this.fetchUsersWithGoals(params),
    ]);
    const goalsByUserId = new Map(usersWithGoals.map((user) => [user.id, user.goals]));
    const mergedUsers = users.map((user) => ({
      ...user,
      goals: goalsByUserId.get(user.id) ?? [],
    }));
    const knownIds = new Set(mergedUsers.map((user) => user.id));

    usersWithGoals.forEach((user) => {
      if (!knownIds.has(user.id)) mergedUsers.push(user);
    });

    return sortUsers(mergedUsers);
  }

  async fetchCompetency(period: string): Promise<GoalsCompetencyConfiguration> {
    const response = await this.httpRepository.get<unknown>(getHabitaCompetencyUrl(period));
    return normalizeCompetencyConfiguration(response.data, period);
  }

  async configureCompetency(
    period: string,
    payload: ConfigureCompetencyPayload
  ): Promise<void> {
    await this.httpRepository.put(getHabitaCompetencyUrl(period), payload);
  }

  async updateTeamGoal(period: string, payload: UpdateTeamGoalPayload): Promise<void> {
    const response = await this.httpRepository.put<unknown>(
      getHabitaCompetencyTeamGoalsUrl(period),
      payload
    );
    validateUpdatedTeamGoal(response.data, period, payload);
  }

  async updateUserGoal(userId: string, payload: UpdateUserGoalPayload): Promise<UserGoal> {
    const response = await this.httpRepository.put<unknown>(
      getHabitaUserGoalsUpdateUrl(userId),
      payload
    );
    return normalizeUpdatedUserGoal(response.data, userId, payload);
  }
}
